// 主要的地圖繪製函式
import { reactive } from 'vue'
import twCountyTopo from '@/assets/geojson/twCounty2010merge_updated.topo.json'
import { MapRenderer } from './mapRenderer'
import { ZoomHandler } from './zoomHandler'
import { TooltipManager } from './tooltip'
import { InfoWindowManager } from './infoWindow'
import { IconHandler } from './iconHandler'
import { ClusterManager } from './cluster'
import { EventHandler } from './eventHandler'
import { TYPE_MAP } from './constants'
import * as d3 from 'd3'
import { delay } from '@/utils/tool'

// loading 時的地圖背景
export function drawBackgroundMap (container) {
  // -------------------- 初始化地圖渲染器 --------------------
  const mapRenderer = new MapRenderer(container, twCountyTopo)
  const { path } = mapRenderer.initializeProjection()
  const countyPaths = mapRenderer.drawCountyPaths() // 只畫縣市邊界
  const iconLayer = mapRenderer.createIconLayer() // 創建空的 iconLayer
  const initialTransform = mapRenderer.calculateInitialTransform()
  const { svg, g, width, height } = mapRenderer.getElements()

  // -------------------- 初始化管理器（不處理 siteData） --------------------
  const zoomHandler = new ZoomHandler(svg, g, iconLayer)
  const eventHandler = new EventHandler(null, null, null, iconLayer, null)

  // -------------------- 創建縮放行為 --------------------
  const zoom = zoomHandler.createZoomBehavior(initialTransform)

  // -------------------- 註冊事件（只註冊縣市點擊，不處理 siteData） --------------------
  eventHandler.registerCountyEvents(
    countyPaths,
    null,
    null,
    path,
    width,
    height,
    zoom
  )

  // -------------------- 縣市標籤 --------------------
  mapRenderer.drawCountyLabels() // 只畫縣市名稱 (預覽用 可不畫)

  // -------------------- 將 SVG 添加到容器 --------------------
  mapRenderer.appendToContainer()
}

export function drawD3Map (container, vm, siteData = []) {
  // -------------------- 變數與初始設定 --------------------
  const state = reactive({
    isLocked: false,
    hasChildIcons: false,
    isClickingIcon: false
  })

  function resetState () {
    state.isLocked = false
    state.hasChildIcons = false
    state.isClickingIcon = false
  }

  const DOM = {
    infoWindow: document.getElementById('infoWindow'),
    countyTooltip: document.getElementById('county-tooltip'),
    customTooltip: document.getElementById('custom-tooltip'),
    zoomInBtn: document.querySelector('.fa-plus')?.closest('button'),
    zoomOutBtn: document.querySelector('.fa-minus')?.closest('button'),
    replyButton: document.querySelector('.reply-button'),
    mapTitle: document.querySelector('.tw-map-title'),
    mapTitleV2: vm.$refs.mapTitleV2,
    statusTable: document.querySelector('.tw-map-ifmTips')
  }

  // 將 siteData 添加到 vm 以便事件處理器使用
  vm.siteData = siteData
  vm.typeMap = TYPE_MAP

  // -------------------- 初始化地圖渲染器 --------------------
  const mapRenderer = new MapRenderer(container, twCountyTopo)
  const { projection, path } = mapRenderer.initializeProjection()
  const countyPaths = mapRenderer.drawCountyPaths()
  const iconLayer = mapRenderer.createIconLayer()
  const initialTransform = mapRenderer.calculateInitialTransform()
  const { svg, g, width, height } = mapRenderer.getElements()

  // -------------------- 初始化各種管理器 --------------------
  const zoomHandler = new ZoomHandler(svg, g, iconLayer)
  const tooltipManager = new TooltipManager(DOM)
  const infoWindowManager = new InfoWindowManager(DOM, state)
  const iconHandler = new IconHandler(
    iconLayer,
    projection,
    mapRenderer.geoData,
    vm,
    state,
    tooltipManager,
    infoWindowManager
  )
  const clusterManager = new ClusterManager(projection)
  const eventHandler = new EventHandler(
    DOM,
    state,
    vm,
    iconLayer,
    infoWindowManager,
    iconHandler,
    g
  )

  // -------------------- 創建縮放行為 --------------------
  const zoom = zoomHandler.createZoomBehavior(initialTransform)

  // -------------------- 註冊事件 --------------------
  eventHandler.registerCountyEvents(
    countyPaths,
    tooltipManager,
    zoomHandler,
    path,
    width,
    height,
    zoom,
    clicked
  )
  eventHandler.registerGlobalClickEvents()

  // -------------------- 縣市標籤 --------------------
  mapRenderer.drawCountyLabels()

  // -------------------- 將 SVG 添加到容器 --------------------
  mapRenderer.appendToContainer()

  // -------------------- 標籤控制功能 --------------------
  vm.toggleSiteLabels = (show) => {
    state.showLabels = show
    iconHandler.updateAllLabelsVisibility()
  }

  vm.showAllSiteLabels = () => {
    iconHandler.showSmartLabels(siteData)
  }

  vm.hideAllSiteLabels = () => {
    iconHandler.hideSmartLabels()
  }

  // -------------------- 放大 & 縮小 按鈕 --------------------
  vm.svg = d3.select(container).select('svg')
  vm.zoom = zoom

  // -------------------- 點擊縣市後的處理 --------------------
  function clicked (event, d) {
    if (!event.currentTarget) {
      event.currentTarget = d3
        .select(container)
        .selectAll('path')
        .filter((dd) => dd.properties.COUNTYNAME === d.properties.COUNTYNAME)
        .node()
    }

    const sitesInCounty = eventHandler.handleCountyClick(
      d,
      zoomHandler,
      path,
      width,
      height,
      zoom
    )

    if (!sitesInCounty) return

    // 更新縣市路徑樣式
    countyPaths
      .attr('fill', '#40445725')
      .attr('filter', null)
      .attr('cursor', null)
    d3.select(event.currentTarget).attr('fill', '#404457').attr('filter', null)
    g.selectAll('text').attr('display', 'none')

    // 重新註冊點擊事件
    countyPaths.on('click', function (event, d) {
      event.stopPropagation()
      clicked(event, d)
    })

    // ----------- Icon Layer 與母子 icon 處理 -----------
    // 清掉上一批的所有圖標和聚類元素
    iconLayer.selectAll('*').remove() // 清掉所有元素

    // -------------------- 主程式使用 --------------------
    const clusters = clusterManager.clusterSites(sitesInCounty)
    const allParentIcons = [] // 收集所有繪製的母icon

    clusters.forEach((cluster) => {
      if (cluster.sites.length === 1) {
        const iconGroup = iconHandler.drawParentIcon(cluster.sites[0])
        if (iconGroup) {
          allParentIcons.push(iconGroup)
        }
      } else {
        clusterManager.drawClusterIcon(
          cluster,
          iconLayer,
          svg,
          zoom,
          projection,
          vm,
          iconHandler.drawParentIcon.bind(iconHandler),
          state,
          iconHandler
        )
      }
    })

    // 使用 force simulation 避免母icon重疊
    iconHandler.applyForceSimulation(allParentIcons)
  }

  // -------------------- 關閉即時資訊視窗 --------------------
  infoWindowManager.registerCloseButton(iconLayer, iconHandler)

  // ----------------------- 地圖回復 -----------------------
  vm.resetMap = async () => {
    // 鎖定 cluster 動畫，防止中途點擊展開
    clusterManager.isResetting = true

    // --- Step 1. 清除目前地圖狀態 ---
    resetState()
    countyPaths.attr('fill', '#404457').attr('filter', null)
    g.selectAll('text').attr('display', 'block').attr('fill', '#6e748f')

    DOM.replyButton.style.display = 'none'
    DOM.mapTitleV2.style.display = 'none'
    DOM.statusTable.style.display = 'none'
    DOM.customTooltip.style.display = 'none'
    if (DOM.mapTitle) DOM.mapTitle.style.display = 'block'
    if (DOM.infoWindow) DOM.infoWindow.style.display = 'none'

    iconLayer.selectAll('*').remove()
    vm.selectedCounty = null
    vm.clearSiteNameList()

    // --- Step 2. 等待地圖平移縮放動畫完成 ---
    await new Promise((resolve) => {
      svg
        .transition()
        .duration(300)
        .ease(d3.easeCubicOut)
        .call(zoom.transform, initialTransform)
        .on('end', resolve)
    })

    // --- Step 3. 動畫完成後解除鎖定 ---
    setTimeout(() => {
      clusterManager.isResetting = false
    }, 500)
  }

  // 避免在 reset 動畫期間誤觸觸發 cluster 重繪
  async function waitUntilNotResetting (maxWait = 30) {
    let waitCount = 0
    while (clusterManager.isResetting && waitCount < maxWait) {
      await new Promise((resolve) => setTimeout(resolve, 100))
      waitCount++
    }
    if (clusterManager.isResetting) {
      console.warn('等待 resetMap 完成超時，強制繼續')
      clusterManager.isResetting = false
    }
  }

  // -------------------- 聚焦某案場 --------------------
  vm.focusSiteBySiteId = async (siteId, isAutoplay = false) => {
    try {
      await waitUntilNotResetting()

      // 找 site
      const site = siteData.find((s) => s.id === siteId)
      if (!site) {
        console.warn(`找不到 siteId: ${siteId}`)
        return
      }

      // 找縣市 Feature
      const countyFeature = mapRenderer.geoData.features.find(
        (f) => f.properties.COUNTYNAME === site.county
      )
      if (!countyFeature) {
        console.warn(`找不到縣市 Feature: ${site.county}`)
        return
      }

      // 游標移動到縣市
      // 手動觸發 mouseover/mouseout 顯示 tooltip
      const countyElement = d3
        .select(container)
        .selectAll('path')
        .filter((d) => d.properties.COUNTYNAME === site.county)
        .node()

      if (isAutoplay) {
        // 先把滑鼠移過去
        await vm.moveCursorTo(countyElement)

        // 取得對應 datum
        const d = d3.select(countyElement).datum()

        // 製作 fake event，取得座標以顯示 tooltip
        const fakeEvent = {
          currentTarget: countyElement,
          pageX:
            countyElement.getBoundingClientRect().x +
            window.scrollX +
            countyElement.getBoundingClientRect().width / 2,
          pageY:
            countyElement.getBoundingClientRect().y +
            window.scrollY +
            countyElement.getBoundingClientRect().height / 2
        }

        eventHandler.handleCountyMouseOver(fakeEvent, d, tooltipManager)
        eventHandler.handleCountyMouseMove(fakeEvent)
        await delay(0.5)
        eventHandler.handleCountyMouseOut(fakeEvent, d, tooltipManager) // 離開觸發 mouseout 隱藏 tooltip
      }

      clicked({ currentTarget: countyElement }, countyFeature)

      // --- 1) 找 parent icon ---
      const parentSel = d3
        .select(container)
        .selectAll('.parent-icon')
        .filter(function () {
          return this.getAttribute('data-site-id') === siteId
        })

      if (!parentSel.empty()) {
        const imageNode = parentSel.select('image').node()
        if (imageNode) {
          isAutoplay && (await vm.moveCursorTo(imageNode))
          dispatchClickAt(imageNode)
        }

        // 如果 parent 展開了 child，直接點 child
        const childNode = d3
          .select(container)
          .selectAll('.child-icon')
          .filter(function () {
            // 案場若多個類型 只呈現順位第一個 (site.types[0]) ex ['mysqlInfo', 'powerInfo'] 取 'mysqlInfo'
            // 因 api+滑鼠移動 需要等待不好掌控輪播時間, 只取第一個
            return this.getAttribute('data-site-type') === site.types[0]
          })
          .node()

        if (childNode) {
          isAutoplay && (await vm.moveCursorTo(childNode))
          dispatchClickAt(childNode)
        }

        return
      }

      // --- 2) 找 cluster ---
      const clusterSel = d3
        .select(container)
        .selectAll('.cluster-rect')
        .filter(function () {
          const ids = (this.getAttribute('data-cluster-ids') || '').split(',')
          return ids.includes(siteId)
        })

      if (!clusterSel.empty()) {
        const clusterNode = clusterSel.node()
        isAutoplay && (await vm.moveCursorTo(clusterNode))
        dispatchClickAt(clusterNode)

        // cluster 展開後直接找 parent
        const parentSel = d3
          .select(container)
          .selectAll('.parent-icon')
          .filter(function () {
            return this.getAttribute('data-site-id') === siteId
          })

        if (!parentSel.empty()) {
          const imageNode = parentSel.select('image').node()
          if (imageNode) {
            isAutoplay && (await vm.moveCursorTo(imageNode))
            dispatchClickAt(imageNode)
          }
        }

        // 然後點 child
        const childSel = d3
          .select(container)
          .selectAll('.child-icon')
          .filter(function () {
            return this.getAttribute('data-site-id') === siteId
          })

        if (!childSel.empty()) {
          const childNode = childSel.node()
          if (childNode) {
            isAutoplay && (await vm.moveCursorTo(childNode))
            dispatchClickAt(childNode)
          }
        }

        return
      }

      console.warn(`找不到 parent 或 cluster: ${siteId}`)
    } catch (err) {
      console.error('focusSiteBySiteId error:', err)
    }
  }

  // 返回點擊函式供外部使用
  return { clicked }
}

// ---------- 輔助：向元素 dispatch click（帶 clientX/clientY） ----------
function dispatchClickAt (node, clientX = 0, clientY = 0) {
  const ev = new MouseEvent('click', {
    bubbles: true,
    cancelable: true,
    view: window,
    clientX: clientX,
    clientY: clientY
  })
  node.dispatchEvent(ev)
}
